import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Users, ArrowLeft, Search, UserX, UserCheck, LogOut as LogOutIcon, Trash2, ShieldCheck, ShieldOff } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const C = {
  bg: '#0F0F0F',
  surf: '#161616',
  card: '#1C1C1C',
  bdr: '#2A2A2A',
  accent: '#D97757',
  accentS: 'rgba(217,119,87,0.10)',
  accentB: 'rgba(217,119,87,0.28)',
  green: '#4CAF82',
  greenS: 'rgba(76,175,130,0.12)',
  red: '#E05C5C',
  redS: 'rgba(224,92,92,0.10)',
  text: '#F0EDE8',
  muted: '#888',
  dim: '#2A2A2A',
};

const G = `
  @keyframes spin { to { transform: rotate(360deg) } }
  @keyframes fadeUp { from { opacity:0; transform:translateY(8px) } to { opacity:1; transform:translateY(0) } }
`;

function Spinner({ size = 18 }) {
  return <div style={{ width: size, height: size, border: `1.5px solid ${C.bdr}`, borderTopColor: C.accent, borderRadius: '50%', animation: 'spin .7s linear infinite' }} />;
}

function StatCard({ label, value, color = C.text }) {
  return (
    <div style={{ background: C.card, border: `0.5px solid ${C.bdr}`, borderRadius: 10, padding: '14px 16px' }}>
      <div style={{ fontSize: 12, color: C.muted, fontFamily: 'JetBrains Mono, monospace', textTransform: 'uppercase', letterSpacing: 0.7, marginBottom: 5 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 500, color, lineHeight: 1 }}>{value}</div>
    </div>
  );
}

function ConfirmBanner({ user, action, onConfirm, onCancel, working }) {
  const labels = {
    disable: { title: 'Disable account?', desc: `${user.email} will be logged out and cannot sign in until re-enabled.`, btn: 'Yes, disable', color: C.red },
    enable: { title: 'Re-enable account?', desc: `${user.email} will be able to sign in again.`, btn: 'Yes, enable', color: C.green },
    promote: { title: 'Grant admin?', desc: `${user.email} will get full admin access.`, btn: 'Yes, make admin', color: C.accent },
    demote: { title: 'Revoke admin?', desc: `${user.email} will no longer have admin access.`, btn: 'Yes, revoke admin', color: C.red },
    revoke: { title: 'Force logout?', desc: `Kill all active sessions for ${user.email}. They will need to sign in again.`, btn: 'Yes, force logout', color: C.red },
    delete: { title: 'Delete user PERMANENTLY?', desc: `${user.email} and ALL their data (sessions, briefs, drafts) will be deleted. This cannot be undone.`, btn: 'Yes, delete forever', color: C.red },
  };
  const l = labels[action];
  return (
    <div style={{ background: C.card, border: `0.5px solid ${l.color}`, borderRadius: 10, padding: 16, marginBottom: 16, animation: 'fadeUp .2s ease' }}>
      <div style={{ fontSize: 15, fontWeight: 500, color: l.color, marginBottom: 5 }}>{l.title}</div>
      <div style={{ fontSize: 13.5, color: C.muted, marginBottom: 12, lineHeight: 1.5 }}>{l.desc}</div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          onClick={onConfirm}
          disabled={working}
          data-testid="confirm-action-btn"
          style={{ background: l.color, color: '#fff', border: 'none', padding: '7px 14px', borderRadius: 6, fontSize: 12.5, fontWeight: 500, cursor: working ? 'wait' : 'pointer', opacity: working ? 0.7 : 1, display: 'flex', alignItems: 'center', gap: 6 }}
        >
          {working && <Spinner size={12} />} {l.btn}
        </button>
        <button onClick={onCancel} disabled={working} style={{ background: 'transparent', color: C.muted, border: `0.5px solid ${C.bdr}`, padding: '7px 14px', borderRadius: 6, fontSize: 12.5, cursor: 'pointer' }}>
          Cancel
        </button>
      </div>
    </div>
  );
}

function ActionButton({ icon: Icon, label, onClick, color = C.muted, danger = false }) {
  return (
    <button
      onClick={onClick}
      title={label}
      data-testid={`action-${label.toLowerCase().replace(/\s+/g, '-')}`}
      style={{
        background: 'transparent',
        border: `0.5px solid ${C.bdr}`,
        borderRadius: 6,
        padding: '5px 9px',
        color,
        fontSize: 11,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 5,
        transition: 'all .15s',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = danger ? C.red : color;
        e.currentTarget.style.color = danger ? C.red : color;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = C.bdr;
        e.currentTarget.style.color = color;
      }}
    >
      <Icon size={12} strokeWidth={1.8} /> {label}
    </button>
  );
}

export default function AdminPage({ currentUser }) {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [pending, setPending] = useState(null); // { user, action }
  const [working, setWorking] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [u, s] = await Promise.all([
        fetch(`${BACKEND_URL}/api/admin/users`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/admin/stats`, { credentials: 'include' }),
      ]);
      if (u.status === 403) {
        setError('You do not have admin access.');
        setLoading(false);
        return;
      }
      if (!u.ok || !s.ok) throw new Error('Failed to load admin data');
      setUsers(await u.json());
      setStats(await s.json());
    } catch (err) {
      setError(err.message || 'Failed to load admin data');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const performAction = async () => {
    if (!pending) return;
    const { user, action } = pending;
    setWorking(true);
    try {
      let url, method, body;
      if (action === 'disable' || action === 'enable') {
        url = `${BACKEND_URL}/api/admin/users/${user.user_id}`;
        method = 'PATCH';
        body = JSON.stringify({ disabled: action === 'disable' });
      } else if (action === 'promote' || action === 'demote') {
        url = `${BACKEND_URL}/api/admin/users/${user.user_id}`;
        method = 'PATCH';
        body = JSON.stringify({ is_admin: action === 'promote' });
      } else if (action === 'revoke') {
        url = `${BACKEND_URL}/api/admin/users/${user.user_id}/revoke-sessions`;
        method = 'POST';
      } else if (action === 'delete') {
        url = `${BACKEND_URL}/api/admin/users/${user.user_id}`;
        method = 'DELETE';
      }
      const res = await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, credentials: 'include', body });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Action failed');
      }
      setPending(null);
      await fetchAll();
    } catch (err) {
      alert(`Failed: ${err.message}`);
    }
    setWorking(false);
  };

  const filtered = users.filter((u) => {
    const q = search.toLowerCase().trim();
    if (!q) return true;
    return u.email?.toLowerCase().includes(q) || u.name?.toLowerCase().includes(q);
  });

  return (
    <>
      <style>{G}</style>
      <div style={{ minHeight: '100vh', background: C.bg, color: C.text, fontFamily: 'Inter, sans-serif' }}>
        {/* Top Bar */}
        <div style={{ borderBottom: `1px solid ${C.bdr}`, padding: '14px 28px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, background: 'rgba(15,15,15,0.97)', zIndex: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button onClick={() => navigate('/')} data-testid="admin-back-btn" style={{ background: 'transparent', border: `0.5px solid ${C.bdr}`, borderRadius: 6, padding: '6px 11px', color: C.muted, cursor: 'pointer', fontSize: 12.5, display: 'flex', alignItems: 'center', gap: 6 }}>
              <ArrowLeft size={13} strokeWidth={1.8} /> Back to app
            </button>
            <div style={{ width: 1, height: 22, background: C.bdr }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Shield size={17} color={C.accent} strokeWidth={2} />
              <span style={{ fontSize: 16, fontWeight: 600, letterSpacing: '-.3px' }}>Admin Console</span>
              <span style={{ fontSize: 11, color: C.muted, fontFamily: 'JetBrains Mono, monospace', background: C.surf, border: `0.5px solid ${C.bdr}`, padding: '2px 7px', borderRadius: 4 }}>{currentUser?.email}</span>
            </div>
          </div>
        </div>

        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '28px' }}>
          {error && (
            <div style={{ background: C.redS, border: `0.5px solid ${C.red}`, borderRadius: 8, padding: 14, color: C.red, fontSize: 13.5, marginBottom: 16 }}>
              {error}
            </div>
          )}

          {/* Stats */}
          {stats && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 10, marginBottom: 24 }}>
              <StatCard label="Total users" value={stats.total_users} />
              <StatCard label="Admins" value={stats.admin_users} color={C.accent} />
              <StatCard label="Disabled" value={stats.disabled_users} color={stats.disabled_users > 0 ? C.red : C.text} />
              <StatCard label="Active now" value={stats.active_now} color={C.green} />
              <StatCard label="New this week" value={stats.new_this_week} />
              <StatCard label="Content sessions" value={stats.total_content_sessions} />
            </div>
          )}

          {/* Users header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Users size={17} color={C.muted} strokeWidth={1.8} />
              <span style={{ fontSize: 16, fontWeight: 500 }}>Users</span>
              <span style={{ fontSize: 12, color: C.muted, fontFamily: 'JetBrains Mono, monospace' }}>({filtered.length}{search ? ` of ${users.length}` : ''})</span>
            </div>
            <div style={{ position: 'relative' }}>
              <Search size={14} color={C.muted} strokeWidth={1.8} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)' }} />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by email or name..."
                data-testid="admin-search-input"
                style={{ width: 280, padding: '7px 11px 7px 31px', background: C.surf, border: `0.5px solid ${C.bdr}`, borderRadius: 7, color: C.text, fontSize: 13, outline: 'none' }}
              />
            </div>
          </div>

          {/* Pending action confirm */}
          {pending && <ConfirmBanner user={pending.user} action={pending.action} onConfirm={performAction} onCancel={() => setPending(null)} working={working} />}

          {/* Users table */}
          {loading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 60, gap: 10, color: C.muted, fontSize: 13 }}>
              <Spinner size={20} /> Loading users...
            </div>
          ) : (
            <div style={{ background: C.card, border: `0.5px solid ${C.bdr}`, borderRadius: 10, overflow: 'hidden' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.4fr 1fr 1fr 2.4fr', gap: 10, padding: '11px 16px', background: C.surf, borderBottom: `0.5px solid ${C.bdr}`, fontSize: 11, fontFamily: 'JetBrains Mono, monospace', color: C.muted, textTransform: 'uppercase', letterSpacing: 0.6 }}>
                <div>User</div>
                <div>Joined</div>
                <div>Sessions</div>
                <div>Status</div>
                <div style={{ textAlign: 'right' }}>Actions</div>
              </div>
              {filtered.length === 0 && (
                <div style={{ padding: 40, textAlign: 'center', color: C.muted, fontSize: 13 }}>No users found.</div>
              )}
              {filtered.map((u) => (
                <div
                  key={u.user_id}
                  data-testid={`user-row-${u.email}`}
                  style={{ display: 'grid', gridTemplateColumns: '2fr 1.4fr 1fr 1fr 2.4fr', gap: 10, padding: '13px 16px', borderBottom: `0.5px solid ${C.bdr}`, alignItems: 'center', opacity: u.disabled ? 0.5 : 1 }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 500, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 7 }}>
                      {u.name || u.email?.split('@')[0]}
                      {u.is_current && <span style={{ fontSize: 9, color: C.accent, fontFamily: 'JetBrains Mono, monospace', background: C.accentS, border: `0.5px solid ${C.accentB}`, padding: '1px 5px', borderRadius: 3 }}>YOU</span>}
                    </div>
                    <div style={{ fontSize: 12, color: C.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.email}</div>
                  </div>
                  <div style={{ fontSize: 12, color: C.muted, fontFamily: 'JetBrains Mono, monospace' }}>
                    {u.created_at ? new Date(u.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                  </div>
                  <div style={{ fontSize: 13, color: C.text }}>
                    {u.content_sessions_count}
                    <span style={{ fontSize: 11, color: C.muted, marginLeft: 4 }}>· {u.active_sessions_count} live</span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {u.is_admin && (
                      <span style={{ fontSize: 10, color: C.accent, fontFamily: 'JetBrains Mono, monospace', background: C.accentS, border: `0.5px solid ${C.accentB}`, padding: '1px 6px', borderRadius: 3, width: 'fit-content' }}>ADMIN</span>
                    )}
                    {u.disabled ? (
                      <span style={{ fontSize: 10, color: C.red, fontFamily: 'JetBrains Mono, monospace', background: C.redS, border: `0.5px solid rgba(224,92,92,0.3)`, padding: '1px 6px', borderRadius: 3, width: 'fit-content' }}>DISABLED</span>
                    ) : (
                      <span style={{ fontSize: 10, color: C.green, fontFamily: 'JetBrains Mono, monospace', padding: '1px 6px', width: 'fit-content' }}>Active</span>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 5, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                    {!u.is_current && (
                      <>
                        {u.disabled ? (
                          <ActionButton icon={UserCheck} label="Enable" onClick={() => setPending({ user: u, action: 'enable' })} color={C.green} />
                        ) : (
                          <ActionButton icon={UserX} label="Disable" onClick={() => setPending({ user: u, action: 'disable' })} danger />
                        )}
                        {u.is_admin ? (
                          <ActionButton icon={ShieldOff} label="Revoke admin" onClick={() => setPending({ user: u, action: 'demote' })} danger />
                        ) : (
                          <ActionButton icon={ShieldCheck} label="Make admin" onClick={() => setPending({ user: u, action: 'promote' })} color={C.accent} />
                        )}
                        {u.active_sessions_count > 0 && (
                          <ActionButton icon={LogOutIcon} label="Force logout" onClick={() => setPending({ user: u, action: 'revoke' })} />
                        )}
                        <ActionButton icon={Trash2} label="Delete" onClick={() => setPending({ user: u, action: 'delete' })} danger />
                      </>
                    )}
                    {u.is_current && (
                      <span style={{ fontSize: 11, color: C.muted, fontStyle: 'italic' }}>Self-actions disabled</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
